<div
  bind:this={popoverRoot}
  bind:clientWidth={popoverRect.width}
  bind:clientHeight={popoverRect.height}
  tabindex="-1"
  class="variations-popover"
  style={`
    top: ${getPositionTop(popoverRect, selectedNodeRect)}px; 
    left: ${getPositionLeft(popoverRect, selectedNodeRect)}px;`
  }
  on:keydown={onKeyDown}
>
  {#each $variationPopover.entries as entry, index}
    <div
      class={`row ${is(index, $variationPopover.selectedIndex) ? 'selected' : ''}`}
      on:mouseenter={() => eventHandlers.selectPopoverMove(index)}
      on:click={() => changeSelectedNode(entry.move)}
      on:keydown={() => {}}
    >
      <span class="icon-wrap">
        {#if is(index, $variationPopover.selectedIndex)}
          <div class="icon-font-chess move-arrow-right icon" />
        {/if}
      </span>

      {#if entry.move}
        <San san={entry.move.san} color={getColorAsWord(entry.move.color)} {textType}/>

        {#if entry.trailingSan}
          <San
            san={entry.trailingSan}
            color={getColorAsWord(getOppositeColor(entry.move.color))}
            {textType}
            figurineClassName="trailing-san-figurine"
          />
        {/if}
      {/if}
    </div>
    {/each}
</div>

<script lang="ts">
import { onMount } from 'svelte';
import type { EventHandlers } from 'components/move-list/state-machine/types';
import type { Data } from 'src/utils/svelte/data';
import type { VariationPopoverProps } from 'components/move-list/store/types';
import { is } from 'src/utils/svelte/assertions';
import type { Board } from 'chessboard/model/board/types';
import San from 'components/move-list/components/san.svelte';
import { getColorAsWord } from 'chessboard/model/game/get-color-as-word';
import { getPositionTop, getPositionLeft } from 'components/move-list/helpers/get-menu-position';
import { getOppositeColor } from 'chessboard/model/game/get-opposite-color';

export let eventHandlers: EventHandlers;
export let variationPopover: Data<VariationPopoverProps>;
export let selectedNodeRect: DOMRect;
export let textType: Data<Board.MoveListDisplayTypes>;

let popoverRoot: HTMLElement | null = null; // bound on mount (read only)
const popoverRect = { width: 0, height: 0 }; // bound on mount (read only)

onMount(() => {
  document.addEventListener('click', handleOutsideClick);
  return () => {
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('keydown', onKeyDown, { capture: true });
  };
});

// need reference to `isOpen` so we don't react to $variationPopover reference changes.
$: popoverOpen = $variationPopover.isOpen;
$: if (popoverOpen) {
  document.addEventListener('keydown', onKeyDown, { capture: true });
} else {
  document.removeEventListener('keydown', onKeyDown, { capture: true });
}

/**
 * on key down handler
 */
function onKeyDown(e) {
  e.preventDefault();

  const keyCodes = {
    DOWN: 40,
    ENTER: 13,
    RIGHT: 39,
    UP: 38,
  };

  if (!$variationPopover.isOpen || Object.values(keyCodes).indexOf(e.keyCode) === -1) {
    return;
  }

  e.stopImmediatePropagation();

  const idxOfSelected = $variationPopover.selectedIndex ?? 0;

  switch (e.keyCode) {
    case keyCodes.UP: {
      const prevMoveItem = $variationPopover.entries?.[idxOfSelected - 1];
      if (prevMoveItem) {
        eventHandlers.selectPopoverMove(idxOfSelected - 1);
      }
      break;
    }
    case keyCodes.DOWN: {
      const nextMoveItem = $variationPopover.entries?.[idxOfSelected + 1];
      if (nextMoveItem) {
        eventHandlers.selectPopoverMove(idxOfSelected + 1);
      }
      break;
    }
    case keyCodes.ENTER:
    case keyCodes.RIGHT: {
      const selectedIds = $variationPopover.entries?.[idxOfSelected]?.move?.ids;
      if (selectedIds) {
        eventHandlers.selectNode(selectedIds);
      }
      break;
    }
    default:
      break;
  }
}

/**
 * on outside click handler
 */
function handleOutsideClick(event) {
  if (popoverRoot === null || popoverRoot.contains(event.target)) {
    return;
  }

  eventHandlers.closeVariationsPopover();
}

/**
 * changes the selected node
 */
function changeSelectedNode(node) {
  eventHandlers.selectNode(node.ids);
}
</script>

<style src="./popover.scss"></style>
